import type { CSSProperties } from 'react';
import { KeyRound, Flame } from 'lucide-react';

// ─── Shared facility-key glyph ──────────────────────────────────────────────────────────────────
// Replaces the raw emoji stored in facility_keys.icon with one of three line icons, render-time
// only -- never writes back to the stored `icon` value (Part 1, 2026-10-08 Facility Keys board
// redesign). Used by both the Option E pegboard card and its side panel so the two never drift.

export type KeyGlyphKind = 'key' | 'keypad' | 'flame';

// Emoji -> glyph mapping, confirmed against the live DB (read-only
// `SELECT icon, COUNT(*) FROM facility_keys GROUP BY icon`, 2026-10-08):
//   🔑 (117 rows) -> key    -- plain key, the default/by-far-most-common value
//   🔐 (3 rows)   -> key    -- "locked with key" is still conceptually a KEY icon, not a keypad
//   🔒 (3 rows)   -> keypad -- plain padlock, no key shown -- closest existing emoji to a
//                              keyless/coded entry point, so this is the bucket that maps to it
//   ⭐ (1 row, area "Fire Panel") -> flame -- exactly the star-on-Fire-Panel case called out in
//                              the spec
//   🏠 (1 row, area "Off Site Shed") -> unmapped -> falls through to 'key' (the spec's own
//                              "unknown values -> KeyRound" rule)
const ICON_MAP: Record<string, KeyGlyphKind> = {
  '🔑': 'key',
  '🗝': 'key',
  '🗝️': 'key',
  '🔐': 'key',
  '🔒': 'keypad',
  '⭐': 'flame',
};

function resolveKind(icon?: string | null, nameHint?: string | null): KeyGlyphKind {
  if (icon && ICON_MAP[icon]) return ICON_MAP[icon];
  // Name-hint fallback for any future/unseen icon value, same spirit as the spec's own
  // star/"Fire Panel" example -- matched generically by name instead of only by emoji.
  const n = (nameHint ?? '').toLowerCase();
  if (n.includes('fire')) return 'flame';
  if (n.includes('keypad') || n.includes('code pad') || n.includes('coded')) return 'keypad';
  return 'key';
}

function KeypadDots({ size, className, style }: { size: number; className?: string; style?: CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} style={style}>
      {[6, 12, 18].flatMap((x) => [5, 11, 17].map((y) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={1.7} />
      )))}
    </svg>
  );
}

export function KeyGlyph({ icon, name, size = 20, className, style }: {
  icon?: string | null;
  name?: string | null;
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const kind = resolveKind(icon, name);
  if (kind === 'flame') return <Flame size={size} className={className} style={style} />;
  if (kind === 'keypad') return <KeypadDots size={size} className={className} style={style} />;
  return <KeyRound size={size} className={className} style={style} />;
}

// Icon color/bg for the color pairing below -- the caller passes dedicated
// --peg-{tier}-icon-color/--peg-{tier}-icon-bg var() references for GM/NM (defined
// in global.css :root + light block), not the column's own tint, since light theme
// gives the icon halo its own distinct color from the hook column (dark happens to
// reuse the same literal value for both today, but they're separate variables).
// NOM/SM and the flame override are themselves var() references into the same
// global.css tier-color block (--peg-nom-icon-*/--peg-flame-icon-*) rather than
// hardcoded literals, so light can override them too (2026-10-10, Part 3) -- dark's
// values there are unchanged (amber for NOM/SM, red for flame, byte-identical to
// what used to be hardcoded directly here).
export function getKeyGlyphTint(
  tier: 'GM' | 'NM' | 'SM' | 'NOM',
  tierTextByTier: Partial<Record<'GM' | 'NM', string>>,
  tierBgByTier: Partial<Record<'GM' | 'NM', string>>,
  icon?: string | null,
  name?: string | null,
): { kind: KeyGlyphKind; color: string; bg: string } {
  const kind = resolveKind(icon, name);
  if (kind === 'flame') return { kind, color: 'var(--peg-flame-icon-color)', bg: 'var(--peg-flame-icon-bg)' };
  if (tier === 'GM' && tierTextByTier.GM) return { kind, color: tierTextByTier.GM, bg: tierBgByTier.GM! };
  if (tier === 'NM' && tierTextByTier.NM) return { kind, color: tierTextByTier.NM, bg: tierBgByTier.NM! };
  return { kind, color: 'var(--peg-nom-icon-color)', bg: 'var(--peg-nom-icon-bg)' }; // NOM and SM (SM has no tier tint of its own per the Option E spec either)
}
